import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  kycStatus: string;
  countryCode?: string | null;
  city?: string | null;
  preferredLanguage: string;
  preferredCurrency: string;
  foundingMember: boolean;
  ipmeStatus: string;
  avatarUrl?: string | null;
  phone?: string | null;
}

interface AppState {
  // Auth
  user: User | null;
  token: string | null;
  setAuth: (user: User, token: string) => void;
  logout: () => void;

  // Language
  language: string;
  setLanguage: (lang: string) => void;

  // Currency
  currency: string;
  setCurrency: (curr: string) => void;

  // Location
  userLocation: { countryCode: string; city: string } | null;
  setUserLocation: (loc: { countryCode: string; city: string }) => void;

  // Cart
  cart: { dealId: number; quantity: number }[];
  addToCart: (dealId: number, quantity: number) => void;
  removeFromCart: (dealId: number) => void;
  clearCart: () => void;

  // Notifications
  unreadCount: number;
  setUnreadCount: (count: number) => void;

  // UI
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Auth
      user: null,
      token: null,
      setAuth: (user, token) => {
        set({ user, token });
        localStorage.setItem('ipo_token', token);
      },
      logout: () => {
        set({ user: null, token: null, cart: [] });
        localStorage.removeItem('ipo_token');
      },

      // Language
      language: 'en',
      setLanguage: (lang) => set({ language: lang }),

      // Currency
      currency: 'USD',
      setCurrency: (curr) => set({ currency: curr }),

      // Location
      userLocation: null,
      setUserLocation: (loc) => set({ userLocation: loc }),

      // Cart
      cart: [],
      addToCart: (dealId, quantity) => {
        const cart = get().cart;
        const existing = cart.find(c => c.dealId === dealId);
        if (existing) {
          set({ cart: cart.map(c => c.dealId === dealId ? { ...c, quantity: c.quantity + quantity } : c) });
        } else {
          set({ cart: [...cart, { dealId, quantity }] });
        }
      },
      removeFromCart: (dealId) => {
        set({ cart: get().cart.filter(c => c.dealId !== dealId) });
      },
      clearCart: () => set({ cart: [] }),

      // Notifications
      unreadCount: 0,
      setUnreadCount: (count) => set({ unreadCount: count }),

      // UI
      sidebarOpen: false,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      theme: 'light',
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'ipo-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        language: state.language,
        currency: state.currency,
        userLocation: state.userLocation,
        cart: state.cart,
        theme: state.theme,
      }),
    }
  )
);

// Translation helper
export function useTranslation() {
  const language = useStore((s) => s.language);
  const translations: Record<string, Record<string, string>> = {
    en: {
      'nav.home': 'Home',
      'nav.deals': 'Browse Deals',
      'nav.categories': 'Categories',
      'nav.suppliers': 'For Suppliers',
      'nav.ipme': 'IPME Program',
      'nav.dashboard': 'Dashboard',
      'nav.admin': 'Admin',
      'hero.title': 'Your Global Marketplace',
      'hero.subtitle': 'AI-curated deals from verified suppliers worldwide',
      'hero.browse': 'Browse Deals',
      'hero.supplier': 'Become a Supplier',
      'escrow.trust': 'Your money is held in a secure Escrow account until delivery is confirmed. Shop with absolute confidence.',
      'search.placeholder': 'Search deals, categories, suppliers...',
      'deal.price': 'Price',
      'deal.supplier': 'Supplier',
      'deal.category': 'Category',
      'deal.order': 'Order Now',
      'deal.wishlist': 'Save',
      'deal.share': 'Share',
      'deal.ai_match': 'AI Match',
      'deal.verified': 'Verified Supplier',
      'footer.about': 'About IPO',
      'footer.suppliers': 'Supplier Center',
      'footer.ipme': 'IPME Program',
      'footer.support': 'Support & Security',
      'common.loading': 'Loading...',
      'common.error': 'An error occurred',
      'common.submit': 'Submit',
      'common.cancel': 'Cancel',
      'common.save': 'Save',
      'common.delete': 'Delete',
      'common.edit': 'Edit',
      'common.view': 'View',
      'common.search': 'Search',
      'common.filter': 'Filter',
      'common.sort': 'Sort',
      'auth.login': 'Sign In',
      'auth.register': 'Create Account',
      'auth.logout': 'Sign Out',
      'auth.email': 'Email',
      'auth.password': 'Password',
      'auth.forgot': 'Forgot password?',
      'auth.no_account': "Don't have an account?",
      'auth.has_account': 'Already have an account?',
      'auth.register_now': 'Register now',
      'auth.login_now': 'Sign in',
      'auth.first_name': 'First Name',
      'auth.last_name': 'Last Name',
      'auth.phone': 'Phone Number',
      'auth.role': 'I am a',
      'auth.role.buyer': 'Buyer',
      'auth.role.supplier': 'Supplier',
      'auth.role.member': 'Member/Affiliate',
      'auth.terms': 'I agree to the Terms of Service and Privacy Policy',
      'dashboard.overview': 'Overview',
      'dashboard.orders': 'My Orders',
      'dashboard.wishlist': 'Wishlist',
      'dashboard.messages': 'Messages',
      'dashboard.settings': 'Settings',
      'dashboard.analytics': 'Analytics',
      'dashboard.deals': 'My Deals',
      'dashboard.earnings': 'Earnings',
      'dashboard.links': 'Referral Links',
      'dashboard.commissions': 'Commissions',
      'dashboard.points': 'Points',
      'dashboard.tasks': 'Tasks',
      'admin.users': 'Users',
      'admin.suppliers': 'Suppliers',
      'admin.deals': 'Deals',
      'admin.escrow': 'Escrow',
      'admin.analytics': 'Analytics',
      'admin.settings': 'Settings',
      'category.agriculture': 'Agriculture',
      'category.electronics': 'Electronics',
      'category.medical': 'Medical Equipment',
      'category.energy': 'Renewable Energy',
      'category.realestate': 'Real Estate',
      'category.vehicles': 'Vehicles',
      'category.construction': 'Construction',
      'category.smartcities': 'Smart Cities',
      'category.food': 'Food & Beverage',
      'category.industrial': 'Industrial',
      'category.consulting': 'Consulting',
      'category.education': 'Education',
      'category.security': 'Security',
      'category.transport': 'Transport',
      'category.art': 'Art & Antiques',
      ' escrow.secure': 'Secure Escrow',
      'escrow.description': 'All payments held safely until delivery confirmed',
      'order.status.pending': 'Pending',
      'order.status.paid': 'Paid',
      'order.status.shipped': 'Shipped',
      'order.status.delivered': 'Delivered',
      'order.status.completed': 'Completed',
      'order.status.disputed': 'Disputed',
      'order.status.refunded': 'Refunded',
      'supplier.verification.gold': 'Gold Verified',
      'supplier.verification.verified': 'Verified',
      'supplier.verification.premium': 'Premium',
      'currency.usd': 'USD',
      'currency.eur': 'EUR',
      'currency.gbp': 'GBP',
    },
    fr: {
      'nav.home': 'Accueil',
      'nav.deals': 'Parcourir',
      'nav.categories': 'Categories',
      'nav.suppliers': 'Pour Fournisseurs',
      'nav.ipme': 'Programme IPME',
      'nav.dashboard': 'Tableau de Bord',
      'nav.admin': 'Admin',
      'hero.title': 'Votre Marche Mondial',
      'hero.subtitle': 'Offres selectionnees par IA de fournisseurs verifies dans le monde entier',
      'hero.browse': 'Parcourir',
      'hero.supplier': 'Devenir Fournisseur',
      'escrow.trust': 'Votre argent est conserve sur un compte en fiducie securise jusqu\'a confirmation de la livraison.',
      'search.placeholder': 'Rechercher des offres, categories, fournisseurs...',
      'deal.price': 'Prix',
      'deal.supplier': 'Fournisseur',
      'deal.category': 'Categorie',
      'deal.order': 'Commander',
      'deal.wishlist': 'Enregistrer',
      'deal.share': 'Partager',
      'deal.ai_match': 'IA Match',
      'deal.verified': 'Fournisseur Verifie',
      'auth.login': 'Connexion',
      'auth.register': 'Creer un Compte',
      'auth.logout': 'Deconnexion',
      'auth.email': 'Email',
      'auth.password': 'Mot de passe',
      'auth.forgot': 'Mot de passe oublie?',
      'auth.no_account': 'Pas encore de compte?',
      'auth.has_account': 'Deja un compte?',
      'auth.register_now': 'Inscrivez-vous',
      'auth.login_now': 'Connectez-vous',
      'auth.first_name': 'Prenom',
      'auth.last_name': 'Nom',
      'auth.phone': 'Telephone',
      'auth.role': 'Je suis un',
      'auth.role.buyer': 'Acheteur',
      'auth.role.supplier': 'Fournisseur',
      'auth.role.member': 'Membre/Affilie',
      'dashboard.overview': 'Vue d\'ensemble',
      'dashboard.orders': 'Mes Commandes',
      'dashboard.wishlist': 'Liste de souhaits',
      'dashboard.settings': 'Parametres',
    },
    sw: {
      'nav.home': 'Nyumbani',
      'nav.deals': 'Vilivyopo',
      'nav.categories': 'Kategoria',
      'nav.suppliers': 'Kwa Wasambazaji',
      'nav.dashboard': 'Dashibodi',
      'hero.title': 'Soko Lako la Kimataifa',
      'hero.subtitle': 'Mikataba iliyochaguliwa na AI kutoka kwa wasambazaji waliodhibitishwa ulimwenguni',
      'hero.browse': 'Vinjari',
      'auth.login': 'Ingia',
      'auth.register': 'Fungua Akaunti',
      'auth.logout': 'Toka',
    },
    es: {
      'nav.home': 'Inicio',
      'nav.deals': 'Explorar',
      'nav.categories': 'Categorias',
      'nav.suppliers': 'Para Proveedores',
      'nav.dashboard': 'Panel',
      'hero.title': 'Tu Mercado Global',
      'hero.subtitle': 'Ofertas seleccionadas por IA de proveedores verificados worldwide',
      'hero.browse': 'Explorar',
      'auth.login': 'Iniciar Sesion',
      'auth.register': 'Crear Cuenta',
      'auth.logout': 'Cerrar Sesion',
    },
    pt: {
      'nav.home': 'Inicio',
      'nav.deals': 'Navegar',
      'nav.categories': 'Categorias',
      'nav.suppliers': 'Para Fornecedores',
      'nav.dashboard': 'Painel',
      'hero.title': 'Seu Mercado Global',
      'hero.subtitle': 'Ofertas curadas por IA de fornecedores verificados em todo o mundo',
      'hero.browse': 'Navegar',
      'auth.login': 'Entrar',
      'auth.register': 'Criar Conta',
      'auth.logout': 'Sair',
    },
    ar: {
      'nav.home': 'الرئيسية',
      'nav.deals': 'تصفح',
      'nav.categories': 'الفئات',
      'nav.suppliers': 'للموردين',
      'nav.dashboard': 'لوحة التحكم',
      'hero.title': 'سوقك العالمي',
      'hero.subtitle': 'صفقات مختارة بالذكاء الاصطناعي من موردين موثقين حول العالم',
      'hero.browse': 'تصفح',
      'auth.login': 'تسجيل الدخول',
      'auth.register': 'إنشاء حساب',
      'auth.logout': 'تسجيل الخروج',
    },
  };

  const t = (key: string) => {
    const langDict = translations[language] || translations.en;
    return langDict[key] || translations.en[key] || key;
  };

  return { t, language };
}
